import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useListbox } from "../../hooks/useListbox";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";
import { MenuOption } from "../Menu/MenuOption";
import { ICON_INSET_END, ICON_PAD_END, ICON_SIZE, type InputSize, PAD_X, SIZE_CLASSES } from "./Input";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;

  invalid?: boolean;

  size?: InputSize;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

export function Select({
  options,
  placeholder,
  value,
  defaultValue,
  onChange,
  invalid = false,
  size = "lg",
  disabled = false,
  required = false,
  id,
  name,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: Readonly<SelectProps>) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const selectedValue = isControlled ? value : internalValue;

  // Shared by useListbox (outside-click) and usePopoverPosition (placement):
  // the panel is portaled to document.body, outside containerRef.
  const panelRef = useRef<HTMLDivElement | null>(null);

  const {
    isOpen,
    setIsOpen,
    highlightedIndex,
    setHighlightedIndex,
    selectedIndex,
    containerRef,
    triggerRef,
    optionRefs,
    listboxId,
    optionId,
    openAt,
    commitSelection,
    handleTriggerKeyDown,
  } = useListbox({
    id: id ?? "select",
    options,
    value: selectedValue,
    onChange: (next) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
    },
    disabled,
    panelRef,
  });

  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const { style: panelStyle } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef: triggerRef,
    matchAnchorWidth: true,
    panelRef,
  });

  return (
    <div ref={containerRef} className="relative isolate w-full min-w-0 flex-1">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={isOpen ? optionId(highlightedIndex) : undefined}
        aria-invalid={invalid || undefined}
        aria-label={
          ariaLabel ?? (ariaLabelledBy ? undefined : (selectedOption?.label ?? placeholder ?? "Select an option"))
        }
        aria-labelledby={ariaLabelledBy}
        onClick={() => (isOpen ? setIsOpen(false) : openAt(selectedIndex >= 0 ? selectedIndex : 0))}
        onKeyDown={handleTriggerKeyDown}
        className={[
          "block w-full truncate bg-surface-elevated text-left",
          SIZE_CLASSES[size],
          PAD_X[size].start,
          ICON_PAD_END[size],
          selectedOption ? "text-content-primary" : "text-content-tertiary",
          "border",
          invalid ? "border-error" : isOpen ? "border-border-focus" : "border-border-input",
          "btn-focus-ring",
          "cursor-pointer outline-hidden transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          size === "lg" ? "shadow-[0_1px_1px_rgba(0,0,0,0.05)]" : "",
        ].join(" ")}
      >
        {selectedOption?.label ?? placeholder}
      </button>
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute ${ICON_INSET_END[size]} top-1/2 z-10 inline-flex -translate-y-1/2 items-center justify-center text-content-tertiary`}
      >
        <ChevronDown size={ICON_SIZE[size]} />
      </span>
      {isOpen &&
        // Portaled: the panel is position:fixed, and inside a Card (its own
        // stacking context) later siblings, e.g. form buttons, painted over it.
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            id={listboxId}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            style={panelStyle}
            className="z-[110] max-h-64 overflow-auto rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg"
          >
            {options.map((option, index) => (
              <MenuOption
                key={option.value}
                role="option"
                id={optionId(index)}
                optionRef={(node) => {
                  optionRefs.current[index] = node;
                }}
                highlighted={index === highlightedIndex}
                selected={index === selectedIndex}
                trailing={false}
                onHighlight={() => setHighlightedIndex(index)}
                onSelect={() => commitSelection(index)}
              >
                {option.label}
              </MenuOption>
            ))}
          </div>,
          document.body,
        )}
      {name && <input type="hidden" name={name} required={required} value={selectedValue ?? ""} />}
    </div>
  );
}
